/**
 * Job Manager for Paper2Ink Web Client (§8, SAAS-410).
 *
 * Responsibilities:
 * - Submit conversion jobs to API with deterministic parameters and idempotency keys
 * - Poll job states (queued -> claimed -> processing -> completed/failed/cancelled)
 * - Persist pending job in localStorage to support page reload and browser resume
 * - Expose cooperative cancel and retry capabilities
 * - Resolve explicit conversion result branches vs head advancements
 * - Emit real-time state updates to UI listeners
 */

import type { Paper2InkApiClient } from "../api/client.ts";
import type {
  ConversionResponse,
  JobResponse,
  BranchResponse,
  RevisionResponse,
} from "../api/types.ts";
import type { CIMDocument } from "../editor/model.ts";
import type { BranchWorkflowManager } from "../editor/branchWorkflow.ts";
import type {
  JobManagerOptions,
  JobState,
  JobStorageAdapter,
  JobUpdateListener,
  PendingJobRecord,
  ConversionJobResult,
} from "./types.ts";

class DefaultStorageAdapter implements JobStorageAdapter {
  getItem(key: string): string | null {
    if (typeof localStorage !== "undefined") {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    }
    return null;
  }

  setItem(key: string, value: string): void {
    if (typeof localStorage !== "undefined") {
      try {
        localStorage.setItem(key, value);
      } catch {
        // storage disabled or quota exceeded
      }
    }
  }

  removeItem(key: string): void {
    if (typeof localStorage !== "undefined") {
      try {
        localStorage.removeItem(key);
      } catch {
        // storage disabled
      }
    }
  }
}

export class JobManager {
  private readonly api: Paper2InkApiClient;
  private readonly storage: JobStorageAdapter;
  private readonly storageKey: string;
  private readonly pollIntervalMs: number;
  private readonly maxPollIntervalMs: number;
  private readonly pollBackoffFactor: number;

  private activeJob: PendingJobRecord | null = null;
  private pollTimer: any = null;
  private listeners: Set<JobUpdateListener> = new Set();
  private isPolling = false;

  constructor(api: Paper2InkApiClient, options: JobManagerOptions = {}) {
    this.api = api;
    this.storage = options.storage || new DefaultStorageAdapter();
    this.storageKey = options.storageKey || "p2i_pending_job";
    this.pollIntervalMs = options.pollIntervalMs || 1000;
    this.maxPollIntervalMs = options.maxPollIntervalMs || 3000;
    this.pollBackoffFactor = options.pollBackoffFactor || 1.2;
  }

  public getActiveJob(): PendingJobRecord | null {
    return this.activeJob;
  }

  public onJobUpdate(listener: JobUpdateListener): () => void {
    this.listeners.add(listener);
    if (this.activeJob) {
      listener(this.activeJob);
    }
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    if (!this.activeJob) return;
    for (const listener of this.listeners) {
      try {
        listener(this.activeJob);
      } catch (err) {
        console.error("Error in JobUpdateListener:", err);
      }
    }
  }

  private persistActiveJob(): void {
    if (this.activeJob) {
      this.storage.setItem(this.storageKey, JSON.stringify(this.activeJob));
    } else {
      this.storage.removeItem(this.storageKey);
    }
  }

  /**
   * Submit a new conversion job and initiate polling.
   */
  public async submitConversion(
    documentId: string,
    assetId: string,
    options: {
      modelVersion?: string;
      prepareSpec?: Record<string, unknown>;
      overrideFlag?: boolean;
      idempotencyKey?: string;
    } = {}
  ): Promise<PendingJobRecord> {
    this.stopPolling();

    const idempotencyKey =
      options.idempotencyKey ||
      `idem_conv_${documentId.substring(0, 8)}_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

    const convRes = await this.api.documents.createConversion(
      documentId,
      {
        asset_id: assetId,
        model_version: options.modelVersion,
        prepare_spec: options.prepareSpec,
        override_flag: options.overrideFlag,
      },
      idempotencyKey
    );

    const now = Date.now();
    this.activeJob = {
      jobId: convRes.job_id,
      conversionId: convRes.conversion_id,
      documentId: convRes.document_id,
      inputAssetId: convRes.input_asset_id,
      modelVersion: convRes.model_version,
      prepareSpec: options.prepareSpec,
      submittedAt: now,
      updatedAt: now,
      state: "queued",
      attempt: 0,
      maxAttempts: 3,
      phase: "queued",
    };

    this.persistActiveJob();
    this.notifyListeners();
    this.startPolling();

    return this.activeJob;
  }

  /**
   * Check for a saved pending job from previous session and resume polling if
   * non-terminal. Terminal (completed/failed/cancelled) records are cleared
   * without being returned: a finished job must not reopen a dialog on every
   * visit — the result already lives in the document, the job is history.
   */
  public async checkAndResumePendingJob(): Promise<PendingJobRecord | null> {
    const raw = this.storage.getItem(this.storageKey);
    if (!raw) return null;

    try {
      const record = JSON.parse(raw) as PendingJobRecord;

      const terminalStates: JobState[] = ["completed", "failed", "cancelled"];
      if (terminalStates.includes(record.state)) {
        this.storage.removeItem(this.storageKey);
        return null;
      }

      this.activeJob = record;
      this.notifyListeners();

      // Poll once immediately to refresh status after reload
      try {
        const isTerminal = await this.pollOnce();
        if (!isTerminal) {
          this.startPolling();
        }
      } catch (err) {
        console.warn("Failed to check status of restored job:", err);
        this.startPolling();
      }
      return this.activeJob;
    } catch (err) {
      console.warn("Corrupt pending job record in storage, removing:", err);
      this.storage.removeItem(this.storageKey);
      return null;
    }
  }

  /**
   * Cancel the active or specified job.
   */
  public async cancelJob(jobId?: string): Promise<PendingJobRecord> {
    const targetJobId = jobId || this.activeJob?.jobId;
    if (!targetJobId) {
      throw new Error("No active job to cancel");
    }

    const cancelRes = await this.api.jobs.cancel(targetJobId);
    this.stopPolling();

    if (this.activeJob && this.activeJob.jobId === targetJobId) {
      this.activeJob.state = "cancelled";
      this.activeJob.updatedAt = Date.now();
      this.activeJob.phase = cancelRes.phase || "cancelled";
      this.persistActiveJob();
      this.notifyListeners();
      return this.activeJob;
    }

    const updated: PendingJobRecord = {
      jobId: cancelRes.id,
      conversionId: cancelRes.conversion_id || "",
      documentId: this.activeJob?.documentId || "",
      inputAssetId: this.activeJob?.inputAssetId || "",
      modelVersion: this.activeJob?.modelVersion || "",
      submittedAt: Date.parse(cancelRes.created_at) || Date.now(),
      updatedAt: Date.now(),
      state: "cancelled",
      attempt: cancelRes.attempt,
      maxAttempts: cancelRes.max_attempts,
    };
    return updated;
  }

  /**
   * Retry a failed or cancelled conversion job.
   */
  public async retryJob(jobId?: string): Promise<PendingJobRecord> {
    const current = this.activeJob;
    if (!current) {
      throw new Error("No job available to retry");
    }

    return await this.submitConversion(current.documentId, current.inputAssetId, {
      modelVersion: current.modelVersion,
      prepareSpec: current.prepareSpec,
    });
  }

  /**
   * Dismiss the active job from storage and state.
   */
  public dismissActiveJob(): void {
    this.stopPolling();
    this.activeJob = null;
    this.storage.removeItem(this.storageKey);
    this.notifyListeners();
  }

  /**
   * Start polling active job status with backoff.
   */
  private startPolling(): void {
    if (this.isPolling) return;
    this.isPolling = true;

    let currentInterval = this.pollIntervalMs;

    const tick = async () => {
      if (!this.isPolling || !this.activeJob) return;

      const isTerminal = await this.pollOnce();
      if (isTerminal) {
        this.stopPolling();
        return;
      }

      currentInterval = Math.min(
        currentInterval * this.pollBackoffFactor,
        this.maxPollIntervalMs
      );
      this.pollTimer = setTimeout(tick, currentInterval);
      if (typeof this.pollTimer?.unref === "function") {
        this.pollTimer.unref();
      }
    };

    this.pollTimer = setTimeout(tick, this.pollIntervalMs);
    if (typeof this.pollTimer?.unref === "function") {
      this.pollTimer.unref();
    }
  }

  public stopPolling(): void {
    this.isPolling = false;
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /**
   * Single poll cycle. Returns true if terminal state reached.
   */
  public async pollOnce(): Promise<boolean> {
    if (!this.activeJob) return true;

    try {
      const jobRes = await this.api.jobs.get(this.activeJob.jobId);
      const rawState = jobRes.state.toLowerCase();
      const state = (rawState === "succeeded" ? "completed" : rawState) as JobState;

      this.activeJob.state = state;
      this.activeJob.attempt = jobRes.attempt;
      this.activeJob.maxAttempts = jobRes.max_attempts;
      this.activeJob.phase = jobRes.phase;
      this.activeJob.lastErrorCode = jobRes.last_error_code;
      this.activeJob.updatedAt = Date.now();

      if (state === "completed") {
        // Check conversion details for result_revision_id and branches
        let resultRevId = (jobRes as any).result_revision_id;
        try {
          const convRes = await this.api.documents.getConversion(
            this.activeJob.documentId,
            this.activeJob.conversionId
          );
          if (convRes && convRes.result_revision_id) {
            resultRevId = convRes.result_revision_id;
          }

          // Check if explicit conversion branch was created
          const branchList = await this.api.documents.listBranches(this.activeJob.documentId);
          const convBranch = branchList.items.find(
            (b) =>
              b.kind === "conversion" &&
              b.tip_revision_id === resultRevId
          );

          if (convBranch) {
            this.activeJob.resultBranchId = convBranch.id;
            this.activeJob.isDetachedBranch = true;
          } else {
            this.activeJob.isDetachedBranch = false;
          }
        } catch (convErr) {
          console.warn("Failed to query conversion result branches:", convErr);
        }
        this.activeJob.resultRevisionId = resultRevId;
      }

      this.persistActiveJob();
      this.notifyListeners();

      return ["completed", "failed", "cancelled"].includes(state);
    } catch (err: any) {
      if (err.status === 404) {
        // Job was deleted or tombstoned
        this.activeJob.state = "cancelled";
        this.activeJob.lastErrorCode = "job_not_found";
        this.persistActiveJob();
        this.notifyListeners();
        return true;
      }
      return false;
    }
  }

  /**
   * Fetch the finalized CIM document from the completed conversion revision (§8, SAAS-410).
   */
  public async fetchResultCIMDocument(
    record?: PendingJobRecord
  ): Promise<ConversionJobResult> {
    const job = record || this.activeJob;
    if (!job || job.state !== "completed" || !job.resultRevisionId) {
      throw new Error("Job is not completed or missing result revision ID");
    }

    const rev = await this.api.documents.getRevision(job.documentId, job.resultRevisionId);
    const cimDoc = rev.cim_document as unknown as CIMDocument;
    if (!cimDoc) {
      throw new Error("Revision does not contain a valid CIM document payload");
    }

    let branch: BranchResponse | null = null;
    if (job.resultBranchId) {
      try {
        branch = await this.api.documents.getBranch(job.documentId, job.resultBranchId);
      } catch (err) {
        console.warn("Could not retrieve result branch details:", err);
      }
    }

    return {
      cimDocument: cimDoc,
      revision: rev,
      branch,
      isDetachedBranch: Boolean(job.isDetachedBranch),
    };
  }

  /**
   * Apply conversion result to the active BranchWorkflowManager.
   * If an explicit conversion branch was created (or local edits exist),
   * attaches it as a dedicated conversion branch without overwriting local uncommitted edits.
   */
  public async applyResultToWorkflow(
    workflow: BranchWorkflowManager,
    record?: PendingJobRecord
  ): Promise<{ branchId: string; revisionId: string; document: CIMDocument }> {
    const { cimDocument, revision, branch, isDetachedBranch } =
      await this.fetchResultCIMDocument(record);

    const applied = workflow.applyConversionResult(
      cimDocument,
      revision.id,
      isDetachedBranch,
      branch ? branch.id : undefined
    );

    return {
      branchId: applied.branch.id,
      revisionId: applied.revisionId,
      document: applied.document,
    };
  }
}
